import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSidebar } from "@/context/SidebarContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, X, Search, User, LogOut, Settings, LayoutDashboard, Shield, Bookmark, Store, PanelLeft, Home } from "lucide-react";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { getContent } = useSiteContent();
  const { toggleSidebar, isExpanded } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // Hide navbar search on homepage (it has its own hero search)
  const showNavSearch = location.pathname !== "/";
  const isVendor = user?.account_type === "vendor";
  const isCamp = user?.account_type === "camp";
  const isAdmin = user?.account_type === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Scroll to camp director section on homepage
  const scrollToCampDirector = () => {
    const doScroll = () => {
      const section = document.getElementById("camp-director-section");
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      // Wait for navigation and page load, then scroll
      // Use multiple attempts to ensure element is ready
      const attempts = [300, 600, 1000];
      attempts.forEach(delay => {
        setTimeout(doScroll, delay);
      });
    } else {
      doScroll();
    }
  };

  const getDashboardLink = () => {
    if (isAdmin) return "/admin";
    if (isVendor) return "/vendor/dashboard";
    return "/dashboard";
  };
  const dashboardLink = getDashboardLink();

  return (
    <>
      {/* Mobile Menu Overlay - closes menu when tapped outside */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 top-16 bg-black/30 z-[45]"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}
      
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#1E3A2F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Vendor/Camp/Admin layout: centered logo with sidebar toggle */}
        {(isVendor || isCamp || isAdmin) ? (
          <div className="relative flex items-center justify-center h-16">
            {/* Left side - Home button (shown on non-home pages) */}
            {showNavSearch && (
              <div className="absolute left-0">
                <Link 
                  to="/" 
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F0E8] hover:bg-[#D4E6D4] text-[#1E3A2F] font-medium transition-colors text-sm"
                  data-testid="home-btn-auth"
                >
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">Home</span>
                </Link>
              </div>
            )}
            
            {/* Centered Logo - icon first, then text */}
            <Link 
              to="/" 
              className="flex items-center gap-2 group" 
              data-testid="logo-link"
            >
              <picture>
                <source srcSet="/logo-40.webp 1x, /logo-80.webp 2x" type="image/webp" />
                <img 
                  src="/logo-small.png" 
                  alt="" 
                  width="40"
                  height="40"
                  className="w-10 h-10 rounded-xl transform group-hover:scale-105 transition-transform"
                />
              </picture>
              <span className="text-xl font-bold text-[#1E3A2F]">
                Camp<span className="text-[#2D5A3D]">Yid</span>
              </span>
            </Link>
            {/* Right side controls - notification bell and sidebar toggle */}
            <div className="absolute right-0 flex items-center gap-2">
              {/* Notification Bell for camp directors */}
              {isCamp && <NotificationBell />}
              {/* Sidebar toggle button
                  On mobile: hamburger menu icon to expand sidebar
                  On desktop: panel icon for sidebar lock/unlock */}
              <button
                className="p-2 rounded-lg hover:bg-green-50"
                onClick={toggleSidebar}
                data-testid="sidebar-toggle"
              >
                {/* Mobile: show hamburger menu / X based on sidebar state */}
                <span className="md:hidden">
                  {isExpanded ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </span>
                {/* Desktop: show panel icon */}
                <span className="hidden md:inline">
                  {isExpanded ? <X className="w-6 h-6" /> : <PanelLeft className="w-6 h-6" />}
                </span>
              </button>
            </div>
          </div>
        ) : (
          /* Non-vendor layout: standard left-aligned logo with right-side controls */
          <div className="flex items-center justify-between h-16">
            {/* Logo - left-aligned with icon first */}
            <Link 
              to="/" 
              className="flex items-center gap-2 group" 
              data-testid="logo-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <picture>
                <source srcSet="/logo-40.webp 1x, /logo-80.webp 2x" type="image/webp" />
                <img 
                  src="/logo-small.png" 
                  alt="" 
                  width="40"
                  height="40"
                  className="w-10 h-10 rounded-xl transform group-hover:scale-105 transition-transform"
                />
              </picture>
              <span className="text-xl font-bold text-[#1E3A2F]">
                Camp<span className="text-[#2D5A3D]">Yid</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4 flex-1 max-w-xl mx-8">
              {/* Home button - shown on all pages except home */}
              {showNavSearch && (
                <Link 
                  to="/" 
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F0E8] hover:bg-[#D4E6D4] text-[#1E3A2F] font-medium transition-colors whitespace-nowrap"
                  data-testid="home-btn"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>
              )}
            </div>

            {/* Desktop Auth */}
            <div className="hidden md:flex items-center gap-4">
              {/* Browse All */}
              {!showNavSearch && (
                <Link to="/search" className="nav-link flex items-center gap-2" data-testid="search-link">
                  <Search className="w-4 h-4" />
                  {getContent("nav_browse_all", "Browse All")}
                </Link>
              )}
              {isAuthenticated ? (
                <>
                  {/* Notification Bell - only for non-admin authenticated users */}
                  {!isAdmin && <NotificationBell />}
                  {/* User menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                        <Avatar className="h-10 w-10 border-2 border-[#6B8E6B]">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback className="bg-[#3D6B4A] text-white">
                            {user?.name?.charAt(0)?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="end" forceMount>
                      <div className="flex items-center gap-3 p-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.picture} />
                          <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <p className="text-sm font-medium">{user?.name}</p>
                          <p className="text-xs text-[#2D5A3D] capitalize">{user?.account_type} Account</p>
                        </div>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to={dashboardLink} className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
                          {user?.account_type === "admin" ? <Shield className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}
                          {user?.account_type === "admin" ? "Admin Dashboard" : "Dashboard"}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2 cursor-pointer" data-testid="settings-link">
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="ghost" className="font-semibold" data-testid="login-btn">
                      {getContent("nav_sign_in", "Sign In")}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#3D6B4A] hover:bg-[#1E3A2F] font-semibold rounded-full px-6" data-testid="signup-btn">
                      {getContent("nav_get_started", "Get Started")}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu button - for unauthenticated users only (authenticated camp/vendor users use sidebar) */}
            <div className="md:hidden flex items-center gap-2">
              <button
                className="p-2 rounded-lg hover:bg-green-50"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu - only for unauthenticated users (authenticated users use sidebar) */}
      {mobileMenuOpen && !isVendor && !isCamp && !isAdmin && (
        <div className="md:hidden bg-white border-t border-stone-200 py-4 px-4 space-y-4 relative z-[110]">
          {/* Home button - shown on all pages except home */}
          {showNavSearch && (
            <Link
              to="/"
              className="flex items-center gap-3 p-3 rounded-lg bg-[#E8F0E8] hover:bg-[#D4E6D4]"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-home-btn"
            >
              <Home className="w-5 h-5 text-[#2D5A3D]" />
              <span className="font-medium text-[#1E3A2F]">Home</span>
            </Link>
          )}
          
          <Link
            to="/search"
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-green-50"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-search-link"
          >
            <Search className="w-5 h-5 text-[#2D5A3D]" />
            <span className="font-medium">{getContent("nav_browse_all", "Browse All Listings")}</span>
          </Link>

          <div className="pt-4 mt-4 border-t border-stone-200 space-y-3">
            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="outline" className="w-full" data-testid="mobile-login-btn">
                {getContent("nav_sign_in", "Sign In")}
              </Button>
            </Link>
            <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-[#3D6B4A] hover:bg-[#1E3A2F]" data-testid="mobile-signup-btn">
                {getContent("nav_get_started", "Get Started")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};
